/**
 * api/useLive — the SPA's real-time transport for the fast-moving numbers (equity,
 * P&L, the daily-loss governor and open positions).
 *
 * It prefers the backend SSE stream (`GET /api/stream`, which pushes `overview` +
 * `positions` events every couple of seconds) and transparently FALLS BACK to short
 * polling when the stream can't be used — e.g. EventSource is unavailable, or the
 * bearer token can't authenticate the stream (EventSource sends no `Authorization`
 * header; the demo backend honours the `?token=` query, a same-origin prod cookie
 * would ride automatically, otherwise we poll). Callers get one stable shape and never
 * have to know which transport is live.
 *
 * A slow in-flight poll can never overwrite fresher data: each tick aborts on unmount
 * and the fallback only starts after the stream has demonstrably failed.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { api, streamUrl, UnauthorizedError } from './client';
import type { DecisionRecord, OverviewResponse, PositionsResponse } from './types';

export type Transport = 'connecting' | 'stream' | 'poll';

export interface LiveState {
  readonly overview: OverviewResponse | null;
  readonly positions: PositionsResponse | null;
  /** The most recent committee decision pushed over the stream, if any. */
  readonly latestDecision: DecisionRecord | null;
  readonly transport: Transport;
  readonly error: string | null;
  /** Epoch ms of the last update from either transport. */
  readonly updatedAt: number | null;
}

interface LiveOptions {
  /** Poll cadence (ms) for the fallback transport. */
  readonly pollMs?: number;
  /** Called once when either transport hits a 401 so the shell can log out. */
  readonly onUnauthorized?: () => void;
}

/** How many consecutive stream errors we tolerate before giving up on SSE for good. */
const STREAM_FAIL_LIMIT = 2;

export function useLive(opts: LiveOptions = {}): LiveState {
  const { pollMs = 4000, onUnauthorized } = opts;

  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [positions, setPositions] = useState<PositionsResponse | null>(null);
  const [latestDecision, setLatestDecision] = useState<DecisionRecord | null>(null);
  const [transport, setTransport] = useState<Transport>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);

  const onUnauthRef = useRef(onUnauthorized);
  onUnauthRef.current = onUnauthorized;

  useEffect(() => {
    let disposed = false;
    let es: EventSource | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    let streamFailures = 0;

    const stamp = (): void => {
      if (!disposed) setUpdatedAt(Date.now());
    };

    // ── fallback: short polling ────────────────────────────────────────────
    const poll = async (): Promise<void> => {
      controller = new AbortController();
      try {
        const [ov, pos] = await Promise.all([
          api.overview(controller.signal),
          api.positions(controller.signal),
        ]);
        if (disposed) return;
        setOverview(ov);
        setPositions(pos);
        setError(null);
        stamp();
      } catch (err) {
        if (disposed || (err instanceof DOMException && err.name === 'AbortError')) return;
        if (err instanceof UnauthorizedError) {
          onUnauthRef.current?.();
          return; // stop; the shell unmounts us on logout
        }
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!disposed) pollTimer = setTimeout(() => void poll(), pollMs);
      }
    };

    const startPolling = (): void => {
      if (disposed) return;
      setTransport('poll');
      void poll();
    };

    // ── preferred: SSE stream ──────────────────────────────────────────────
    const startStream = (): void => {
      if (typeof EventSource === 'undefined') {
        startPolling();
        return;
      }
      try {
        es = new EventSource(streamUrl());
      } catch {
        startPolling();
        return;
      }

      // Mark the stream live. Some proxies (and EventSource polyfills) deliver the first
      // named event *before* — or without ever firing — the `open` event, which used to
      // leave the UI wedged on "connecting" even though frames were arriving. So we flip
      // to `stream` on the first sign of life from either `open` OR any data frame, and
      // clear the failure tally so a transient blip mid-session can't accumulate toward a
      // permanent poll fallback (graceful reconnect).
      const markLive = (): void => {
        if (disposed) return;
        streamFailures = 0;
        setTransport('stream');
        setError(null);
      };

      es.addEventListener('open', markLive);

      es.addEventListener('overview', (ev) => {
        if (disposed) return;
        const parsed = parse<OverviewResponse>((ev as MessageEvent).data);
        if (parsed) { markLive(); setOverview(parsed); stamp(); }
      });

      es.addEventListener('positions', (ev) => {
        if (disposed) return;
        const parsed = parse<PositionsResponse>((ev as MessageEvent).data);
        if (parsed) { markLive(); setPositions(parsed); stamp(); }
      });

      es.addEventListener('decision', (ev) => {
        if (disposed) return;
        const parsed = parse<DecisionRecord>((ev as MessageEvent).data);
        if (parsed) { markLive(); setLatestDecision(parsed); }
      });

      es.addEventListener('error', () => {
        if (disposed) return;
        // EventSource retries on its own, but a persistently failing stream (most often
        // an auth failure we can't see the status of) should hand off to polling for good.
        streamFailures += 1;
        if (streamFailures >= STREAM_FAIL_LIMIT) {
          es?.close();
          es = null;
          startPolling();
        }
      });
    };

    startStream();

    return () => {
      disposed = true;
      es?.close();
      controller?.abort();
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [pollMs]);

  return useMemo(
    () => ({ overview, positions, latestDecision, transport, error, updatedAt }),
    [overview, positions, latestDecision, transport, error, updatedAt],
  );
}

function parse<T>(data: unknown): T | null {
  if (typeof data !== 'string') return null;
  try {
    return JSON.parse(data) as T;
  } catch {
    return null;
  }
}
